import { useState, useEffect, useRef, useCallback } from 'react';
import { QrCodeIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import DataTable from '../components/DataTable';
import FilamentForm from '../components/FilamentForm';
import FilamentPresetSelector from '../components/FilamentPresetSelector';
import BarcodeModal from '../components/BarcodeModal';
import { useToast } from '../hooks/useToast';
import { useDebounce } from '../hooks/useDebounce';

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const values = Object.values(name);
    return values.find(v => v) || '';
  }
  return String(name);
};

export default function Filaments() {
  const [filaments, setFilaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPresetSelector, setShowPresetSelector] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [editingFilament, setEditingFilament] = useState(null);
  const [barcode, setBarcode] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [materialExtras, setMaterialExtras] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    material: '',
    materialExtra: '',
    brand: '',
    lowStock: false
  });

  const [autoAddStock, setAutoAddStock] = useState(() => {
    try { return localStorage.getItem('filaments_auto_add_stock') === 'true'; } catch { return false; }
  });
  const autoAddStockRef = useRef(autoAddStock);

  const barcodeInput = useRef('');
  const barcodeTimeout = useRef(null);

  const { showToast } = useToast();

  const debouncedSearch = useDebounce(filters.search);

  const fetchBarcodeInfo = async (barcode) => {
    try {
      const barcodeInfo = (await api.get(`/auth/filaments/barcode/${barcode}`)).data;
      return barcodeInfo
    } catch (error) {
      if (error.status === 404) {
        return null;
      }
      throw error
    }

  }


  const readBarcode = (e) => {

    if (barcodeTimeout.current) {
      clearTimeout(barcodeTimeout.current)
      barcodeTimeout.current = null
    }

    if (e.code === "Enter") {
      if (barcodeInput.current) {
        setBarcode(barcodeInput.current);
      }
      barcodeInput.current = "";
      return;
    }
    if (e.key !== "Shift") {
      barcodeInput.current += e.key;
    }
    barcodeTimeout.current = setInterval(() => (barcodeInput.current = ""), 200);
  }

  const fetchFilaments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add text search
      if (debouncedSearch) {
        params.append('q', debouncedSearch);
      }

      // Add material filter
      if (filters.material) {
        params.append('material', filters.material);
      }

      // Add brand filter
      if (filters.brand) {
        params.append('brand', filters.brand);
      }

      // Add extra filter
      if (filters.materialExtra) {
        params.append('extra', filters.materialExtra);
      }

      const queryString = params.toString();
      const url = queryString ? `/auth/filaments?${queryString}` : '/auth/filaments';

      const response = await api.get(url);
      let filaments = response.data.data;

      // Apply client-side low stock filter
      if (filters.lowStock) {
        filaments = filaments.filter(f => f.currentStock <= f.minimumStock);
      }

      setFilaments(filaments);
    } catch (err) {
      setError('Failed to fetch filaments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.material, filters.brand, filters.materialExtra, filters.lowStock]);

  useEffect(() => {
    fetchMaterials();
    fetchMaterialExtras();
    fetchBrands();
  }, []);

  useEffect(() => {
    if (showForm || showPresetSelector || showBarcodeModal) return;
    window.addEventListener('keydown', readBarcode);
    return () => window.removeEventListener('keydown', readBarcode);
  }, [showForm, showPresetSelector, showBarcodeModal]);

  useEffect(() => {
    fetchFilaments();
  }, [fetchFilaments]);

  const [barcodeResult, setBarcodeResult] = useState(null);

  useEffect(() => {
    async function fetchData() {
      if (barcode) {
        const response = await fetchBarcodeInfo(barcode);
        if (!response?.data) {
          // Not found
          setBarcodeResult({ type: 'not_found' });
          setShowBarcodeModal(true);
        } else if (response.data.type === 'FORK_REQUIRED') {
          // System filament found but farm needs its own fork
          setBarcodeResult({ type: 'fork_required', sourceFilament: response.data.sourceFilament, suggestedData: response.data.suggestedData });
          setShowBarcodeModal(true);
        } else if (response.data.type === 'MATCH') {
          const filament = response.data.filament;
          if (filament.farm) {
            if (autoAddStockRef.current) {
              // Auto-add: skip modal, patch stock directly
              try {
                const newStock = (filament.currentStock || 0) + (filament.weight || 0);
                await api.patch(`/auth/filaments/${filament._id || filament.id}`, { currentStock: newStock });
                await fetchFilaments();
                showToast(`Added ${filament.weight}g to stock for ${filament.fullName || filament.name}`, 'success');
              } catch (err) {
                showToast(err.response?.data?.message || 'Failed to update stock', 'error');
              }
              setBarcode(null);
            } else {
              // Show confirmation modal
              setBarcodeResult({ type: 'owned', data: filament });
              setShowBarcodeModal(true);
            }
          } else {
            // System preset — offer to create from preset
            setBarcodeResult({ type: 'preset', data: filament });
            setShowBarcodeModal(true);
          }
        }
      }
    }
    fetchData();
  }, [barcode])

  const handleAddStock = async (filamentData) => {
    const { _id, id, currentStock, weight, fullName } = filamentData;
    const newStock = (currentStock || 0) + (weight || 0);
    await api.patch(`/auth/filaments/${_id || id}`, { currentStock: newStock });
    await fetchFilaments();
    setBarcode(null);
    setBarcodeResult(null);
    setShowBarcodeModal(false);
    showToast(`Added ${weight}g to stock for ${fullName}`, 'success');
  };

  const createNewFilamentFromBarcode = () => {
    setShowBarcodeModal(false);
    setShowForm(true);
  }

  const createFromPreset = async (presetData) => {
    try {
      // Duplicate the preset filament via API
      const response = await api.post(`/auth/filaments/${presetData._id}/duplicate?from=system`);
      setShowBarcodeModal(false);
      setBarcodeResult(null);
      setBarcode(null);
      // Show edit form with the duplicated filament
      setEditingFilament(response.data.data);
      setShowForm(true);
      showToast('Filament created from preset. You can now customize it.', 'success');
    } catch (err) {
      console.error('Failed to create from preset:', err);
      showToast(err.response?.data?.message || 'Failed to create from preset', 'error');
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await api.get('/auth/materials');
      setMaterials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };

  const fetchMaterialExtras = async () => {
    try {
      const response = await api.get('/auth/material-extras');
      setMaterialExtras(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch material extras:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/auth/filament-brands');
      setBrands(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const handleAutoAddStockChange = (e) => {
    autoAddStockRef.current = e.target.checked;
    setAutoAddStock(e.target.checked);
    try { localStorage.setItem('filaments_auto_add_stock', e.target.checked); } catch { /* ignore */ }
  };

  const handleAdd = () => {
    setEditingFilament(null);
    setShowPresetSelector(true);
  };

  const handleSelectPreset = ({ created = [], skipped = [] }) => {
    setShowPresetSelector(false);
    if (created.length === 1 && skipped.length === 0) {
      setEditingFilament(created[0]);
      setShowForm(true);
      showToast('Filament created from preset. You can now customize it.', 'success');
    } else {
      fetchFilaments();
      if (created.length > 0) {
        showToast(`${created.length} filament${created.length !== 1 ? 's' : ''} added from presets.`, 'success');
      }
    }
    if (skipped.length > 0) {
      showToast(
        `${skipped.length} filament${skipped.length !== 1 ? 's were' : ' was'} skipped — ${skipped.length !== 1 ? 'they already exist' : 'it already exists'} in your stock.`,
        'info'
      );
    }
  };

  const handleCreateFromScratch = () => {
    setShowPresetSelector(false);
    setEditingFilament(null);
    setShowForm(true);
  };

  const handleEdit = (filament) => {
    setEditingFilament(filament);
    setShowForm(true);
  };

  const handleDelete = async (filament) => {
    if (!window.confirm(`Are you sure you want to delete "${filament.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/filaments/${filament._id}`);
      fetchFilaments();
    } catch (err) {
      alert('Failed to delete filament');
      console.error(err);
    }
  };

  const handleDuplicate = async (filament) => {
    try {
      const response = await api.post(`/auth/filaments/${filament._id}/duplicate?from=farm`);
      setEditingFilament(response.data.data);
      setShowForm(true);
      showToast('Filament duplicated. You can now customize it.', 'success');
    } catch (err) {
      alert('Failed to duplicate filament');
      console.error(err);
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingFilament;
    if (isEditing) {
      await api.patch(`/auth/filaments/${editingFilament._id}`, data);
    } else {
      await api.post('/auth/filaments', data);
    }
    setShowForm(false);
    setEditingFilament(null);
    setBarcode(null);
    fetchFilaments();
    showToast(isEditing ? 'Filament updated successfully!' : 'Filament created successfully!', 'success');
  };

  const handleAssign = async (data) => {
    const { filament } = data;
    delete data.filament;
    await api.patch(`/auth/filaments/${filament}`, data)
    fetchFilaments();
    setShowBarcodeModal(false);
    setBarcode(null);
    showToast('Filament assigned successfully!', 'success');
  };

  const columns = [
    { key: 'fullName', label: 'Name' },
    {
      key: 'brand',
      label: 'Brand',
      render: (val) => typeof val === 'object' ? getDisplayName(val.name) : val
    },
    {
      key: 'material',
      label: 'Material',
      render: (val) => typeof val === 'object' ? `${getDisplayName(val.name)} (${val.key})` : val
    },
    {
      key: 'materialExtra',
      label: 'Extra',
      render: (val) => {
        if (val === null) return '-'
        const name = typeof val.name === 'object'
          ? Object.keys(val.name).map((item) => `<strong>${item}:</strong> ${val.name[item]}`).join('<br/>')
          : val.name;
        return `<strong>key:</strong> ${val.key}<br/>${name}`
      }
    },
    {
      key: 'color',
      label: 'Color',
      render: (val) => val ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ backgroundColor: `#${val.hex}`, display: 'inline-block', width: '1rem', height: '1rem', border: '1px solid #ccc' }}></span>
          {getDisplayName(val.name)}
        </div>
      ) : 'N/A'
    },
    {
      key: 'currentStock',
      label: 'Stock (g)',
      render: (val, item) => (
        <span style={{ color: val <= item.minimumStock ? 'red' : 'inherit' }}>
          {val} / {item.minimumStock}
        </span>
      )
    },
    {
      key: 'costKg',
      label: 'Cost/kg',
      render: (val) => val ? `€${val?.toFixed(2)}` : 'N/A'
    },
    {
      key: 'pricePerPrintedKg',
      label: 'Price/kg',
      render: (val) => val ? `€${val?.toFixed(2)}` : 'N/A'
    }
  ];

  // const handleMaterialExtraToggle = (extraId) => {
  //   setFilters(prev => ({
  //     ...prev,
  //     materialExtras: prev.materialExtras.includes(extraId)
  //       ? prev.materialExtras.filter(id => id !== extraId)
  //       : [...prev.materialExtras, extraId]
  //   }));
  // };

  const renderFilters = () => (
    <div className="filters" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, brand, or color..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ flex: '1 1 300px' }}
        />
        <select
          value={filters.material}
          onChange={(e) => setFilters({ ...filters, material: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="">All Materials</option>
          {materials.map((m) => (
            <option key={m._id} value={m._id}>
              {getDisplayName(m.name)} ({m.key})
            </option>
          ))}
        </select>
        <select
          value={filters.materialExtra}
          onChange={(e) => setFilters({ ...filters, materialExtra: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="">All Extras</option>
          {materialExtras.map((m) => (
            <option key={m._id} value={m._id}>
              {getDisplayName(m.name)} ({m.key})
            </option>
          ))}
        </select>
        <select
          value={filters.brand}
          onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="">All Brands</option>
          {brands.map((b) => (
            <option key={b._id} value={b._id}>
              {getDisplayName(b.name)}
            </option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem' }}>
          <input
            type="checkbox"
            checked={filters.lowStock}
            onChange={(e) => setFilters({ ...filters, lowStock: e.target.checked })}
          />
          <span>Low Stock Only</span>
        </label>
      </div>
      {/* {materialExtras.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontWeight: '500', marginRight: '0.5rem' }}>Material Extras:</span>
          {materialExtras.map((extra) => (
            <label
              key={extra._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                backgroundColor: filters.materialExtras.includes(extra._id) ? '#3b82f6' : 'white',
                color: filters.materialExtras.includes(extra._id) ? 'white' : 'black',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="checkbox"
                checked={filters.materialExtras.includes(extra._id)}
                onChange={() => handleMaterialExtraToggle(extra._id)}
                style={{ cursor: 'pointer' }}
              />
              <span>{extra.name.default}</span>
            </label>
          ))}
        </div>
      )} */}
    </div>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div>
      {showPresetSelector ? (
        <FilamentPresetSelector
          onSelectPreset={handleSelectPreset}
          onCreateNew={handleCreateFromScratch}
          onCancel={() => setShowPresetSelector(false)}
        />
      ) : showForm ? (
        <FilamentForm
          filament={editingFilament}
          barcodeInput={barcode}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingFilament(null);
          }}
        />
      ) : showBarcodeModal ? (
        <BarcodeModal
          barcode={barcode}
          barcodeResult={barcodeResult}
          onAssign={handleAssign}
          onAddStock={handleAddStock}
          onCreateFromPreset={createFromPreset}
          onForkFromPreset={createFromPreset}
          onCreateNew={createNewFilamentFromBarcode}
          onCancel={() => {
            setShowBarcodeModal(false);
            setBarcode(null);
            setBarcodeResult(null);
          }}
        />
      ) : (
        <div>
          <div className="flex items-start gap-3 mb-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <QrCodeIcon className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-700 mb-0.5">Barcode Scanner</p>
              <p className="text-slate-500">Use a barcode scanner anywhere on this screen to look up filaments by barcode — scan to add stock or register new filaments.</p>
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoAddStock}
                  onChange={handleAutoAddStockChange}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-slate-600">Automatically add spool weight to stock when scanning a known filament (no confirmation)</span>
              </label>
            </div>
          </div>
          <DataTable
            data={filaments}
            columns={columns}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onAdd={handleAdd}
            renderFilters={renderFilters}
            allowHTML={true}
          />
        </div>
      )}
    </div>
  );
}
